import React from 'react';
import { GuideContent, GuideOpenBody } from '../../../../../interfaces/stay-guide';
import { CopyValueButton } from '../../../../copy-value/copy-value-button';
import { StayKey, t } from '../../../../onboarding/stay/stay-strings';
import { AccessNotice } from './access-notice';
import { GuideH1, useGuideNav } from './guide-frame';
import { langOf, telHref } from './guide-format';
import {
  Accordion,
  CodeValue,
  Eyebrow,
  FaqItem,
  FaqList,
  HostText,
  Muted,
  Panel,
  QuietLink,
  SecondaryAnchor,
  SectionHeading,
  SmallHeading,
  Stack,
  TileGrid,
  ValueLabel,
} from './guide.styled';

const L = 'en' as const;

/** The four "how things work" answers, in the contract's order. */
const HOW: Array<{ key: keyof GuideContent; label: StayKey }> = [
  { key: 'air_conditioning', label: 'guide.airConditioning' },
  { key: 'heating', label: 'guide.heating' },
  { key: 'hot_water', label: 'guide.hotWater' },
  { key: 'rubbish', label: 'guide.rubbish' },
];

/**
 * ESSENTIALS (plan S7.7): the Wi-Fi (its values and copy buttons once the
 * codes are released, else "Available when your door codes unlock"), the host,
 * quiet hours; an accordion of how the home works (only what the host wrote;
 * the first one open — the faq-body pattern, details/summary); the FAQs under
 * "Good to know"; and the house rules the guest signed (S6's rules page).
 */
export const GuideEssentials: React.FC<{ body: GuideOpenBody }> = ({ body }) => {
  const nav = useGuideNav();
  const { guide, access, faqs } = body;
  const lang = langOf(guide.content_language);
  const host = guide.host_name.trim();
  const tel = telHref(guide.host_phone);
  const how = HOW.filter(h => String(guide[h.key] || '').trim());
  const wifi = access && (access.wifi_name || access.wifi_password) ? access : null;

  return (
    <>
      <GuideH1>
        {t(L, 'guide.essentialsTitle')}
      </GuideH1>

      {access ? null : <AccessNotice notice={body.access_notice} zone={body.zone} />}

      <TileGrid>
        <Panel aria-labelledby="guide-tile-wifi" data-guide-tile="wifi">
          <SectionHeading id="guide-tile-wifi">{t(L, 'guide.tileWifi')}</SectionHeading>
          {wifi ? (
            <>
              {wifi.wifi_name ? (
                <Stack>
                  <ValueLabel>{t(L, 'guide.wifiNetwork')}</ValueLabel>
                  <CodeValue>{wifi.wifi_name}</CodeValue>
                  <CopyValueButton
                    value={wifi.wifi_name}
                    label={t(L, 'guide.copyWifiName')}
                    announce={t(L, 'guide.wifiNameCopied')}
                  />
                </Stack>
              ) : null}
              {wifi.wifi_password ? (
                <Stack>
                  <ValueLabel>{t(L, 'guide.wifiPassword')}</ValueLabel>
                  <CodeValue>{wifi.wifi_password}</CodeValue>
                  <CopyValueButton
                    value={wifi.wifi_password}
                    label={t(L, 'guide.copyWifiPassword')}
                    announce={t(L, 'guide.wifiPasswordCopied')}
                  />
                </Stack>
              ) : null}
            </>
          ) : access ? (
            <Muted>{t(L, 'guide.contactHost')}</Muted>
          ) : (
            <Muted>{t(L, 'guide.wifiLater')}</Muted>
          )}
        </Panel>

        {host || tel ? (
          <Panel aria-labelledby="guide-tile-host" data-guide-tile="host">
            <SectionHeading id="guide-tile-host">{t(L, 'guide.tileHost')}</SectionHeading>
            {host ? <SmallHeading as="p">{host}</SmallHeading> : null}
            {tel ? (
              <SecondaryAnchor href={tel}>{t(L, 'guide.callHost', { host: host || t(L, 'guide.yourHost') })}</SecondaryAnchor>
            ) : null}
          </Panel>
        ) : null}

        {guide.quiet_hours ? (
          <Panel aria-labelledby="guide-tile-quiet" data-guide-tile="quiet">
            <SectionHeading id="guide-tile-quiet">{t(L, 'guide.tileQuiet')}</SectionHeading>
            <HostText lang={lang}>{guide.quiet_hours}</HostText>
          </Panel>
        ) : null}
      </TileGrid>

      {how.length ? (
        <Accordion data-guide-how="">
          {how.map((h, i) => (
            <details key={h.key} open={i === 0}>
              <summary>{t(L, h.label)}</summary>
              <div className="guide-details-body">
                <HostText lang={lang}>{String(guide[h.key])}</HostText>
              </div>
            </details>
          ))}
        </Accordion>
      ) : null}

      {faqs.length ? (
        <Stack as="section" aria-labelledby="guide-faq-title">
          <SectionHeading id="guide-faq-title">{t(L, 'guide.goodToKnow')}</SectionHeading>
          <FaqList>
            {faqs.map(f => (
              <FaqItem key={f.id}>
                {f.category ? <Eyebrow lang={lang}>{f.category}</Eyebrow> : null}
                <SmallHeading lang={lang}>{f.question}</SmallHeading>
                <HostText lang={lang}>{f.answer}</HostText>
              </FaqItem>
            ))}
          </FaqList>
        </Stack>
      ) : null}

      {!nav.preview && nav.token ? <QuietLink to={`/s/${nav.token}/rules`}>{t(L, 'guide.houseRules')}</QuietLink> : null}
    </>
  );
};
